import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Person } from '../../interfaces/persons.interface';
import { PersonsService } from '../../services/persons.service';
import { Subject, takeUntil } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

@Component({
  selector: 'persons-table',
  standalone: false,
  templateUrl: './persons-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./persons-table.component.scss']
})
export class PersonsTableComponent implements OnInit, OnDestroy {
  @Input() persons: Person[] = [];
  @Output() personChanged = new EventEmitter<void>();

  private $destroy = new Subject<void>();
  loading = false;
  displayedColumns: string[] = ['name', 'cards', 'actions'];

  constructor(
    private personsService: PersonsService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Don't automatically load persons if they are provided as input
    if (!this.persons || this.persons.length === 0) {
      this.loadPersons();
    }
  }

  ngOnDestroy(): void {
    this.$destroy.next();
    this.$destroy.complete();
  }

  loadPersons(): void {
    this.loading = true;
    this.personsService.getCurrent()
      .pipe(takeUntil(this.$destroy))
      .subscribe({
        next: (data: Person[]) => {
          this.persons = data;
          this.loading = false;
        },
        error: (err) => {
          console.error('Error loading persons:', err);
          this.loading = false;
          this.snackBar.open('Nie udało się pobrać listy mieszkańców.', 'Zamknij', {
            duration: 4000,
            panelClass: ['error-snackbar'],
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          });
        }
      });
  }

  onPersonUpdate(): void {
    this.loadPersons();
    this.personChanged.emit();
  }

  getPersonTypeLabel(type: string): string {
    const typeMap: { [key: string]: string } = {
      'CITIZEN': 'Podatnik',
      'SENIOR': 'Senior',
      'STUDENT': 'Student',
      'CHILDREN': 'Dziecko'
    };
    return typeMap[type] || type;
  }

  viewPersonDetails(person: Person): void {
    // Navigate to person edit page with person ID
    this.router.navigate(['/person-edit', person.id]);
  }

  viewPersonCards(person: Person): void {
    // TODO: Implement person cards view
    this.snackBar.open(`Karty dla: ${person.name} ${person.surname}`, 'Zamknij', {
      duration: 3000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }

  viewPersonFamily(person: Person): void {
    // TODO: Implement person family view
    this.snackBar.open(`Rodzina dla: ${person.name} ${person.surname}`, 'Zamknij', {
      duration: 3000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }

  getActiveCards(person: Person): any[] {
    if (!person.cards) return [];
    return person.cards.filter(card => card.status === 1 ||  card.status === 2 || card.status === 3 || card.status === 4);
  }

  getGronoScore(person: Person): number {
    const entry = person.aggregations?.find(a => a.partner_id === 0);
    return entry?.aggregation_score ?? 0;
  }

  getPartnersScore(person: Person): number {
    return person.aggregations
      ?.filter(a => (a.partner_id ?? 0) > 0)
      .reduce((sum, a) => sum + (a.aggregation_score ?? 0), 0) ?? 0;
  }

  getPartnersCount(person: Person): number {
    return person.aggregations?.filter(a => (a.partner_id ?? 0) > 0).length ?? 0;
  }

  hasAggregationData = (_index: number, person: Person): boolean => {
    if (!person.aggregations?.length) return false;
    return person.aggregations.some(a => (a.aggregation_score ?? 0) > 0);
  };

  addNewPerson(): void {
    // Navigate to person edit page without ID (for new person)
    this.router.navigate(['/person-edit']);
  }
}
